import { NavLink } from "react-router";
import { MapPinned, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";

/** Decorative logistics motif — faint route lines + stop nodes drawn from the
 *  palette's chart tokens (--chart-1..5), so it repaints with the active palette
 *  and works in light/dark. Purely ornamental: aria-hidden, non-interactive.
 *  Not an icon (those come from lucide) — a background illustration, hence the
 *  inline SVG. Colors are tokens, never hex, per the design-system hygiene rule. */
function HeroMotif() {
  const routes = [
    { c: "var(--chart-1)", d: "M 60 180 C 260 120 420 250 640 195 S 980 120 1160 230", nodes: [[60, 180], [640, 195], [1160, 230]] },
    { c: "var(--chart-2)", d: "M 40 360 C 220 300 380 430 580 365 S 920 300 1170 405", nodes: [[40, 360], [580, 365], [1170, 405]] },
    { c: "var(--chart-3)", d: "M 90 545 C 260 485 430 610 640 545 S 980 470 1160 575", nodes: [[90, 545], [430, 585], [1160, 575]] },
    { c: "var(--chart-4)", d: "M 200 70 C 380 160 540 60 720 165 S 1010 265 1185 130", nodes: [[200, 70], [720, 165], [1185, 130]] },
    { c: "var(--chart-5)", d: "M 130 690 C 320 640 520 715 720 645 S 1010 560 1170 660", nodes: [[130, 690], [720, 645], [1170, 660]] },
  ];
  return (
    <svg
      aria-hidden="true"
      className="pointer-events-none absolute inset-0 h-full w-full opacity-10"
      viewBox="0 0 1200 760"
      preserveAspectRatio="xMidYMid slice"
      fill="none"
    >
      {routes.map((r) => (
        <g key={r.d} stroke={r.c} fill={r.c}>
          {/* fill="none" is essential: the paths are open beziers, so without it they
              inherit the group fill and render as filled ribbons instead of lines. */}
          <path d={r.d} fill="none" strokeWidth={3} strokeLinecap="round" strokeOpacity={0.9} />
          {r.nodes.map(([x, y], i) => (
            <circle key={i} cx={x} cy={y} r={i === 0 ? 7 : 5} fillOpacity={0.9} />
          ))}
        </g>
      ))}
    </svg>
  );
}

/** Default landing surface (`/`). A minimal splash — Certu mark, wordmark, a witty
 *  byline, and a single primary CTA into the Shipment Explorer — over a faded,
 *  palette-tinted route motif. The left sidebar is shown expanded here (RootLayout
 *  expands it on `/` and collapses it to the icon rail on the data surfaces). */
export default function HomePage() {
  return (
    <div className="relative flex min-h-0 flex-1 flex-col items-center justify-center overflow-hidden px-6 text-center">
      <HeroMotif />
      {/* Legibility bloom: a soft wash of --background behind the copy so the motif
          reads as a backdrop, not competition. */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0"
        style={{ background: "radial-gradient(ellipse 62% 55% at 50% 46%, var(--background), transparent)" }}
      />

      <div className="relative flex flex-col items-center">
        {/* Certu brand MARK — colored in light, monochrome (inverted white) in dark,
            the same two-<img> swap the sidebar uses. */}
        <img src="/certu-mark.svg" alt="Certu" className="h-16 w-auto dark:hidden" />
        <img src="/certu-mark-mono.svg" alt="Certu" aria-hidden="true" className="hidden h-16 w-auto dark:block dark:invert" />

        <h1 className="mt-6 text-4xl font-semibold tracking-tight sm:text-5xl">Explorer</h1>
        <p className="mt-4 max-w-md text-lg text-balance text-muted-foreground">
          Where freight comes to explain itself.
        </p>

        <div className="mt-9 flex flex-col items-center gap-3">
          <Button asChild size="lg">
            <NavLink to="/shipments">
              <MapPinned />
              Explore shipments
              <ArrowRight />
            </NavLink>
          </Button>
          <NavLink
            to="/orders"
            className="text-sm text-muted-foreground underline-offset-4 transition-colors hover:text-foreground hover:underline"
          >
            or open the Order Explorer
          </NavLink>
        </div>
      </div>
    </div>
  );
}
